import { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useTheme } from '@/contexts/ThemeContext';
import { 
  Sparkles, 
  TrendingUp, 
  Zap,
  Brain,
  Globe,
  MessageSquare,
  Calendar,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';

const Propuesta = () => {
  const { theme } = useTheme();
  const { scrollYProgress } = useScroll();
  
  // Parallax y zoom de fondo
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  const scale = useTransform(scrollYProgress, [0, 0.3], [1, 1.3]);
  
  // Opacidad general del contenido
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.3], [1, 0.5, 0]);
  
  // Separación de logos - Claim entra desde IZQUIERDA y sale hacia DERECHA
  const claimX = useTransform(scrollYProgress, [0, 0.3], [0, 400]);
  const claimOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  
  // Separación de logos - Artificialmente entra desde DERECHA y sale hacia IZQUIERDA
  const artificialmenteX = useTransform(scrollYProgress, [0, 0.3], [0, -400]);
  const artificialmenteOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  
  // Amaya se desvanece y sube
  const amayaY = useTransform(scrollYProgress, [0, 0.3], [0, -100]);
  const amayaOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  
  // Título - SE VA A LA IZQUIERDA
  const titleX = useTransform(scrollYProgress, [0, 0.3], [0, -600]);
  const titleY = useTransform(scrollYProgress, [0, 0.3], [0, 150]);
  const titleOpacity = useTransform(scrollYProgress, [0, 0.25], [1, 0]);
  
  // Subtítulo - SE VA A LA DERECHA (dirección opuesta)
  const subtitleX = useTransform(scrollYProgress, [0, 0.3], [0, 600]);
  const subtitleY = useTransform(scrollYProgress, [0, 0.3], [0, 100]);
  const subtitleOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  
  // Badges - SE VAN HACIA ABAJO CON MOVIMIENTO VISIBLE
  const badgesX = useTransform(scrollYProgress, [0, 0.3], [0, 0]);
  const badgesY = useTransform(scrollYProgress, [0, 0.3], [0, 800]);
  const badgesScale = useTransform(scrollYProgress, [0, 0.3], [1, 0.5]);
  const badgesOpacity = useTransform(scrollYProgress, [0, 0.25, 0.3], [1, 0.8, 0]);
  
  // Scroll indicator se desvanece rápido
  const scrollOpacity = useTransform(scrollYProgress, [0, 0.1], [1, 0]);

  return (
    <div className="relative bg-background overflow-x-hidden">
      {/* Hero Section - Logos + Fondo */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden snap-start">
        {/* Imagen de fondo con parallax y scale */}
        <motion.div 
          className="absolute inset-0 z-0"
          style={{ 
            y,
            scale
          }}
        >
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: 'url(https://customer-assets.emergentagent.com/job_cardealer-ui/artifacts/woh8jh66_mjxopt6i_Fondo_BYD_Seal.png)',
              filter: theme === 'dark' ? 'brightness(0.5)' : 'brightness(0.7)'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/90" />
        </motion.div>

        {/* Contenido del Hero */}
        <motion.div 
          className="relative z-10 text-center px-6 max-w-6xl mx-auto"
          style={{ opacity }}
        >
          {/* Logos superiores - MÁS CHICOS, + en azul */}
          <div className="flex items-center justify-center gap-6 mb-8">
            {/* Logo Claim - 30% MÁS CHICO y CENTRADO */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              style={{ x: claimX, opacity: claimOpacity }}
              className="flex items-center"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_cardealer-ui/artifacts/gkhdrs3g_logo_Claim_1_tinta_para_fondo_oscuro.png"
                alt="Claim Consulting"
                className="h-5 w-auto opacity-90"
              />
            </motion.div>
            
            {/* Plus icon - MÁS GRANDE y AZUL */}
            <motion.div 
              className="text-primary text-4xl font-light"
              style={{ opacity: claimOpacity }}
            >
              +
            </motion.div>
            
            {/* Logo Artificialmente - MÁS CHICO */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              style={{ x: artificialmenteX, opacity: artificialmenteOpacity }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_cardealer-ui/artifacts/m17tjpr5_Logo_Artificialmente_1_tinta_para_fondo_oscuro.png"
                alt="Artificialmente"
                className="h-7 w-auto opacity-90"
              />
            </motion.div>
          </div>
          
          {/* Logo Amaya - MÁS CHICO */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            style={{ y: amayaY, opacity: amayaOpacity }}
            className="mb-8 flex justify-center"
          >
            <img 
              src={`${process.env.PUBLIC_URL}/logo-amaya.svg`}
              alt="Amaya Motors"
              className="h-12 w-auto opacity-95"
            />
          </motion.div>

          {/* Título principal - MOVIMIENTO HORIZONTAL NOTORIO */}
          <motion.h1
            initial={{ opacity: 0, x: 50, y: 30 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            style={{ x: titleX, y: titleY, opacity: titleOpacity }}
            className="text-5xl md:text-7xl lg:text-8xl font-black text-white mb-6 leading-tight"
          >
            TRANSFORMACIÓN<br />
            <span className="text-primary">DIGITAL</span>
          </motion.h1>

          {/* Subtítulo - MOVIMIENTO HORIZONTAL NOTORIO */}
          <motion.p
            initial={{ opacity: 0, x: 50, y: 30 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            style={{ x: subtitleX, y: subtitleY, opacity: subtitleOpacity }}
            className="text-xl md:text-2xl text-white/80 mb-12 max-w-3xl mx-auto"
          >
            Una propuesta estratégica de tecnología e IA para potenciar las operaciones de Amaya Motors
          </motion.p>

          {/* Badges - MOVIMIENTO HORIZONTAL MUY NOTORIO */}
          <motion.div
            initial={{ opacity: 0, x: 50, y: 30 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            style={{ x: badgesX, y: badgesY, scale: badgesScale, opacity: badgesOpacity }}
            className="flex flex-wrap items-center justify-center gap-4 mb-12"
          >
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-full px-6 py-3">
              <span className="text-white font-semibold">6 meses</span>
            </div>
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-full px-6 py-3">
              <span className="text-white font-semibold">Resultados medibles</span>
            </div>
            <div className="bg-primary/20 backdrop-blur-xl border border-primary/40 rounded-full px-6 py-3">
              <span className="text-primary font-semibold">Implementación ágil</span>
            </div>
          </motion.div>

          {/* Scroll indicator - EFECTO DE SALIDA */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            style={{ opacity: scrollOpacity }}
            className="flex flex-col items-center gap-2"
          >
            <span className="text-white/60 text-sm uppercase tracking-wider">Scroll</span>
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <ArrowRight className="h-6 w-6 text-white/60 rotate-90" />
            </motion.div>
          </motion.div>
        </motion.div>
      </section>

      {/* Sección 2: Lo que proponemos */}
      <section className="relative min-h-screen flex items-center justify-center py-20 md:py-32 snap-start">
        <div className="max-w-7xl mx-auto px-6">
          {/* Título de sección */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-6xl font-black text-foreground mb-4">
              LO QUE <span className="text-primary">PROPONEMOS</span>
            </h2>
            <div className="w-24 h-1 bg-primary mx-auto" />
          </motion.div>

          {/* Grid de 2 cards */}
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            {/* Card 1: Descripción general */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative group"
            >
              <div 
                className="relative p-8 lg:p-10 rounded-3xl border transition-all duration-500 h-full"
                style={{
                  background: theme === 'dark'
                    ? 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)'
                    : 'linear-gradient(135deg, rgba(248,250,252,0.9) 0%, rgba(241,245,249,0.8) 100%)',
                  borderColor: theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                  boxShadow: theme === 'light' ? '0 4px 20px rgba(0,0,0,0.08)' : 'none'
                }}
              >
                {/* Icono */}
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 mb-6">
                  <Zap className="h-8 w-8 text-primary" />
                </div>

                {/* Contenido */}
                <h3 className="text-2xl lg:text-3xl font-bold text-foreground mb-4">
                  Una Solución Integral
                </h3>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  La propuesta consiste en desarrollar un ecosistema completo de herramientas y sistemas digitales, potenciados con Inteligencia Artificial, diseñados para mejorar significativamente la productividad, organización, eficiencia operativa y calidad de atención en los procesos clave de Amaya Motors.
                </p>
                <p className="text-lg text-muted-foreground leading-relaxed mt-4">
                  Esta transformación digital no solo optimizará las operaciones internas, sino que también elevará la experiencia del cliente y posicionará a Amaya Motors como líder tecnológico en el sector automotriz.
                </p>
              </div>
            </motion.div>

            {/* Card 2: Lista de servicios */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="relative group"
            >
              <div 
                className="relative p-8 lg:p-10 rounded-3xl border transition-all duration-500 h-full"
                style={{
                  background: theme === 'dark'
                    ? 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)'
                    : 'linear-gradient(135deg, rgba(248,250,252,0.9) 0%, rgba(241,245,249,0.8) 100%)',
                  borderColor: theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                  boxShadow: theme === 'light' ? '0 4px 20px rgba(0,0,0,0.08)' : 'none'
                }}
              >
                {/* Título */}
                <h3 className="text-2xl lg:text-3xl font-bold text-foreground mb-6">
                  Puntualmente:
                </h3>

                {/* Lista de servicios */}
                <div className="space-y-6">
                  {/* Servicio 1 */}
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Brain className="h-5 w-5 text-primary" />
                      </div>
                    </div>
                    <div>
                      <h4 className="font-bold text-foreground mb-2">
                        Plataforma IA para Toma de Usados
                      </h4>
                      <ul className="space-y-1.5 text-sm text-muted-foreground">
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Evaluación automatizada con visión por computadora</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Tasación inteligente basada en mercado real</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Gestión digital de documentación y seguimiento</span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  {/* Servicio 2 */}
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Globe className="h-5 w-5 text-primary" />
                      </div>
                    </div>
                    <div>
                      <h4 className="font-bold text-foreground mb-2">
                        Web Institucional + Micrositios
                      </h4>
                      <ul className="space-y-1.5 text-sm text-muted-foreground">
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Búsqueda global inteligente de inventario</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Micrositio único para cada sucursal</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>UX moderna, responsive y optimizada para conversión</span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  {/* Servicio 3 */}
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                        <MessageSquare className="h-5 w-5 text-primary" />
                      </div>
                    </div>
                    <div>
                      <h4 className="font-bold text-foreground mb-2">
                        Asistente IA para Atención al Cliente
                      </h4>
                      <ul className="space-y-1.5 text-sm text-muted-foreground">
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Integración con Multi Avisos (sujeto a API disponible)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Respuestas automáticas 24/7 sobre inventario</span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  {/* Servicio 4 */}
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Calendar className="h-5 w-5 text-primary" />
                      </div>
                    </div>
                    <div>
                      <h4 className="font-bold text-foreground mb-2">
                        Sistema de Agenda para Service
                      </h4>
                      <ul className="space-y-1.5 text-sm text-muted-foreground">
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Reservas online con calendario inteligente</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Optimización automática de turnos con IA</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Notificaciones y recordatorios automatizados</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Sección 3: EL DESAFÍO (Placeholder) */}
      <section className="relative min-h-screen flex items-center justify-center py-20 md:py-32 snap-start bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-6xl font-black text-foreground mb-8">
              EL <span className="text-primary">DESAFÍO</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              [PLACEHOLDER - Situación actual y oportunidad de transformación digital]
            </p>
          </motion.div>
        </div>
      </section>

      {/* Sección 4: SOLUCIONES DETALLADAS (Placeholder) */}
      <section className="relative min-h-screen flex items-center justify-center py-20 md:py-32 snap-start">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-6xl font-black text-foreground mb-8">
              SOLUCIONES <span className="text-primary">DETALLADAS</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              [PLACEHOLDER - Deep dive en cada servicio propuesto]
            </p>
          </motion.div>
        </div>
      </section>

      {/* Sección 5: IMPLEMENTACIÓN (Placeholder) */}
      <section className="relative min-h-screen flex items-center justify-center py-20 md:py-32 snap-start bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-6xl font-black text-foreground mb-8">
              PLAN DE <span className="text-primary">IMPLEMENTACIÓN</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              [PLACEHOLDER - Timeline y fases del proyecto]
            </p>
          </motion.div>
        </div>
      </section>

      {/* Sección 6: RESULTADOS ESPERADOS (Placeholder) */}
      <section className="relative min-h-screen flex items-center justify-center py-20 md:py-32 snap-start">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-6xl font-black text-foreground mb-8">
              RESULTADOS <span className="text-primary">ESPERADOS</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              [PLACEHOLDER - KPIs, métricas y beneficios esperados]
            </p>
          </motion.div>
        </div>
      </section>

      {/* Sección 7: PRÓXIMOS PASOS (Placeholder) */}
      <section className="relative min-h-screen flex items-center justify-center py-20 md:py-32 snap-start bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-6xl font-black text-foreground mb-8">
              PRÓXIMOS <span className="text-primary">PASOS</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-12">
              [PLACEHOLDER - Call to action y contacto]
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Propuesta;
